/**
 *
 *  TimeSlotsListing
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 23/02/2023.
 *  Copyright (c) 2016-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useState, useRef } from "react";
import { useEvent } from "../../../hooks/useEvent/useEvent";
import useForwardRef from "../../../hooks/useForwardRef/useForwardRef";
// import moment from "moment";

import ContentScroller from "../../ContentScroller/ContentScroller";
import TimeSlotItem from "../TimeSlotItem/TimeSlotItem";
import PrevButton from "../PrevButton/PrevButton";
import NextButton from "../NextButton/NextButton";

// Hooks
import useTimeSlots from "../useTimeSlots/useTimeSlots";
import useContentScrollerControls from "../../../hooks/useContentScrollerControls/useContentScrollerControls";
import useButtonsHidden from "../useButtonsHidden/useButtonsHidden";
import { getScrollTarget } from "../helpers/getScrollTarget";
import useDeviceCapabilities from "../../../hooks/useDeviceCapabilities/useDeviceCapabilities";

// Utils
import { getMomentWithTz } from "../../../utils/date";
import getModifierKey from "../helpers/getModifierKey";

const TIME_SLOT_FORMAT_TIME_PART = "h:mm";
const AUTOSCROLL_TIMEOUT = 1000;

var TimeSlotsListing = ({
  isLoading,
  min,
  max,
  id,
  timeSlots,
  disabled,
  step,
  currentDate,
  format,
  culture,
  region,
  value,
  onSelect,
  preserveDate,
  onScrollerMounted,
  onTimeSlotPress,
}) => {
  const times = useTimeSlots(
    min,
    max,
    timeSlots,
    step,
    value,
    currentDate,
    culture,
    region,
    TIME_SLOT_FORMAT_TIME_PART
  );
  const { supportsTouchCapableExperience } = useDeviceCapabilities(),
    slotsCount = times.length,
    [
      maybeSupportsTouchCapableExperience_pe,
      maybeSetSupportsTouchCapableExperience_fe,
    ] = useState(supportsTouchCapableExperience ? 1 : 0);

  const [scrollerMounted, setScrollerMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const modifiersRef = useRef();
  const scrolledRef = useRef(false);
  const lastVisibleItemIndexesRef = useRef([]);

  useEffect(() => {
    maybeSetSupportsTouchCapableExperience_fe(
      supportsTouchCapableExperience ? 1 : 0
    );
  }, [supportsTouchCapableExperience]);

  const contentScrollerRef = useRef(null),
    [_contentScrollerRef, setContentScrollerRef] =
      useForwardRef(contentScrollerRef);

  const [hiddenPrev, hiddenNext] = useButtonsHidden({
      contentScrollerApi: _contentScrollerRef,
      itemCount: slotsCount,
      isLoading: isLoading,
    }),
    { onPressForward, onPressReverse, scrollTo } = useContentScrollerControls({
      contentScrollerApi: _contentScrollerRef,
      loop: false,
      unit: "slide",
      getScrollTarget: getScrollTarget,
    });

  useEffect(() => {
    if (_contentScrollerRef) {
      setScrollerMounted(true);
      onScrollerMounted == null || onScrollerMounted(_contentScrollerRef);
    }
  }, [onScrollerMounted, _contentScrollerRef]);

  const visibleItemsCount =
    _contentScrollerRef && _contentScrollerRef.getVisibleCount();

  useEffect(() => {
    if (
      !scrolledRef.current // &&
    ) {
      setTimeout(() => {
        var scrollToElement = 0;

        times.forEach((ts, index) => {
          if (
            getMomentWithTz(value, region).format(
              `${TIME_SLOT_FORMAT_TIME_PART} A`
            ) === `${ts.label} ${ts.labelAmPm}`
          ) {
            console.log(ts);

            scrollToElement = index + 1;
          }
        });

        if (!_contentScrollerRef || scrolledRef.current) {
          return;
        }

        var nextElementKey =
          scrollToElement -
          Math.floor(_contentScrollerRef.getVisibleCount() / 2);

        if (nextElementKey < 0) {
          nextElementKey = 0;
        }

        scrollTo({
          nextElementKey: nextElementKey,
          spaceBetweenItems: 0,
          shouldUseScrollIntoView: false,
        });

        setScrolled(true);
        scrolledRef.current = true;
      }, AUTOSCROLL_TIMEOUT);
    }
  }, [scrollerMounted, scrollTo, visibleItemsCount, value, times]);

  const recalcReset = useEvent(() => {
    _contentScrollerRef &&
      _contentScrollerRef.recalc({
        reason: "reset",
      });
  });

  useEffect(() => {
    _contentScrollerRef && recalcReset();
  }, [recalcReset, _contentScrollerRef]);

  const recalcChildrenChangedEvent = useEvent(() => {
    _contentScrollerRef &&
      setTimeout(() =>
        _contentScrollerRef.recalc({
          reason: "children_changed",
        })
      );
  });

  useEffect(() => {
    _contentScrollerRef &&
      maybeSupportsTouchCapableExperience_pe > 0 &&
      recalcChildrenChangedEvent();
  }, [
    recalcChildrenChangedEvent,
    _contentScrollerRef,
    maybeSupportsTouchCapableExperience_pe,
  ]);

  const handleTimeSlotVisibleChange = (key, inView, element) => {
    if (key === 0 && !inView) {
      scrolledRef.current = true;
    }
  };

  return (
    <div>
      <ContentScroller
        autoRecalcOnMount={false}
        controls={
          !isLoading && (
            <PrevButton
              onPress={onPressReverse}
              visible={!hiddenPrev}
              className="md:tbk-bottom-2 md:tbk-left-[-48px]"
            />
          )
        }
        trailingControls={
          !isLoading && (
            <NextButton
              onPress={onPressForward}
              visible={!hiddenNext}
              className="md:tbk-bottom-2 md:tbk-right-[-48px]"
            />
          )
        }
        description=""
        ref={setContentScrollerRef}
        ariaScrollerRole="radiogroup"
        style={{
          position: "relative",
        }}
      >
        {times && times.length
          ? times.map((ts, index) => (
              <TimeSlotItem
                contentScrollerApi={_contentScrollerRef}
                key={`${ts.label} ${ts.labelAmPm}`}
                itemKey={index}
                onTimeSlotVisibleChange={handleTimeSlotVisibleChange}
                onTimeSlotPress={onTimeSlotPress}
                selected={
                  getMomentWithTz(value, region).format(
                    `${TIME_SLOT_FORMAT_TIME_PART} A`
                  ) === `${ts.label} ${ts.labelAmPm}`
                }
              >
                <>
                  {ts.label}
                  <div className="tbk-text-small !tbk-leading-none">
                    {ts.labelAmPm}
                  </div>
                </>
              </TimeSlotItem>
            ))
          : null}
      </ContentScroller>
    </div>
  );
};

export default TimeSlotsListing;
